// Cart Item Remove Handler
// Handles AJAX removal of cart items without page reload

class CartItemRemove {
  constructor() {
    // Cache for variant data to avoid repeated API calls
    this.variantCache = new Map()
    this.cacheExpiry = 5 * 60 * 1000 // 5 minutes
    this.init()
  }

  init() {
    // Handle clicks on remove buttons
    document.addEventListener('click', this.handleRemoveClick.bind(this))
    
    // Listen for quantity changes
    document.addEventListener('cart:quantity', this.handleQuantityChange.bind(this))
    
    // Listen for product additions to cart
    document.addEventListener('ajaxProduct:added', this.handleProductAdded.bind(this))
  }

  async handleRemoveClick(evt) {
    const removeBtn = evt.target.closest('[data-cart-remove]')
    if (!removeBtn) return

    evt.preventDefault()

    const cartItemKey = removeBtn.dataset.cartItemKey
    if (!cartItemKey) return

    try {
      // Show loading state
      this.setLoadingState(removeBtn, true)

      // Make AJAX request to remove item
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: cartItemKey,
          quantity: 0
        })
      })

      if (!response.ok) {
        throw new Error('Failed to remove item')
      }

      const cart = await response.json()

      // Update cart display
      await this.updateCartDisplay()

      // Dispatch cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart },
        bubbles: true
      }))

    } catch (error) {
      console.error('Error removing cart item:', error)
      this.setLoadingState(removeBtn, false)
      this.showFeedback('Failed to remove item. Please try again.', 'error')
    }
  }

  async handleProductAdded(evt) {
    // When a product is added to cart, update item counts
    try {
      if (evt.detail && evt.detail.product && evt.detail.product.sections && evt.detail.product.sections['cart-ajax']) {
        this.updateItemCounts(evt.detail.product.sections['cart-ajax'])
      }
    } catch (error) {
      console.error('Error updating item count after product addition:', error)
    }
  }

  async handleQuantityChange(evt) {
    // When quantity changes, update the cart display
    const [cartItemKey, quantity, quantitySelector] = evt.detail
    
    try {
      // Make AJAX request to update quantity
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: cartItemKey,
          quantity: quantity
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update quantity')
      }

      const cart = await response.json()

      // Update cart display
      await this.updateCartDisplay()

      // Dispatch cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart }
      }))

    } catch (error) {
      console.error('Error updating cart quantity:', error)
      // Reset quantity selector on error
      if (quantitySelector && quantitySelector.input) {
        quantitySelector.input.value = quantitySelector.input.getAttribute('data-original-value') || 1
      }
    }
  }

  setLoadingState(button, isLoading) {
    if (isLoading) {
      button.disabled = true
      button.style.opacity = '0.5'
      button.style.pointerEvents = 'none'
    } else {
      button.disabled = false
      button.style.opacity = '1'
      button.style.pointerEvents = 'auto'
    }
  }

  async updateCartDisplay() {
    try {
      // Fetch updated cart HTML
      const response = await fetch('/?sections=cart-ajax')
      const sections = await response.json()
      
      if (sections['cart-ajax']) {
        // Update cart drawer if it exists
        const cartDrawer = document.querySelector('header-cart-drawer')
        if (cartDrawer && cartDrawer.cartForm) {
          cartDrawer.cartForm.cartMarkup(sections['cart-ajax'])
        }

        // Update main cart page if it exists
        const cartItems = document.querySelector('[data-products]')
        if (cartItems) {
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = sections['cart-ajax']
          const newCartItems = tempDiv.querySelector('[data-products]')
          if (newCartItems) {
            cartItems.innerHTML = newCartItems.innerHTML
          }
        }
        
        // Update item count in header and other places
        this.updateItemCounts(sections['cart-ajax'])

        // Update cart totals
        this.updateCartTotals(sections['cart-ajax'])
      }
    } catch (error) {
      console.error('Error updating cart display:', error)
    }
  }

  updateItemCounts(cartHtml) {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = cartHtml
    const cartAjaxElement = tempDiv.querySelector('[data-item-count][style*="display: none"]')
    
    if (cartAjaxElement) {
      const newItemCount = cartAjaxElement.textContent.trim()
      const itemCountElements = document.querySelectorAll('[data-item-count]')
      
      itemCountElements.forEach(element => {
        if (!element.hasAttribute('style') || element.style.display !== 'none') {
          // Check if this element should have parentheses (like in cart drawer header)
          const parentElement = element.parentElement
          if (parentElement && (parentElement.classList.contains('cart__drawer-title') || 
                               parentElement.tagName.toLowerCase() === 'h2')) {
            element.textContent = `(${newItemCount})`
          } else {
            element.textContent = newItemCount
          }
        }
      })

      // Update cart link bubble
      const cartBubbles = document.querySelectorAll('.cart-link__bubble-num')
      cartBubbles.forEach(bubble => {
        bubble.textContent = newItemCount
      })

      // Show/hide cart bubble based on count
      const cartBubbleContainers = document.querySelectorAll('.cart-link__bubble')
      cartBubbleContainers.forEach(container => {
        if (newItemCount > 0) {
          container.classList.add('cart-link__bubble--visible')
        } else {
          container.classList.remove('cart-link__bubble--visible')
        }
      })
    }
  }

  updateCartTotals(cartHtml) {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = cartHtml
    
    // Update subtotal
    const newSubtotal = tempDiv.querySelector('.cart__subtotal')
    const currentSubtotal = document.querySelector('[data-subtotal]')
    
    if (newSubtotal && currentSubtotal) {
      currentSubtotal.innerHTML = newSubtotal.innerHTML
    }

    // Update discounts
    const newDiscounts = tempDiv.querySelector('.cart__discounts')
    const currentDiscounts = document.querySelector('[data-discounts]')
    
    if (currentDiscounts) {
      if (newDiscounts) {
        currentDiscounts.innerHTML = newDiscounts.innerHTML
      } else {
        currentDiscounts.innerHTML = ''
      }
    }

    // Update original total price
    this.updateOriginalTotal(cartHtml)
  }

  async getVariantData(variantId) {
    const now = Date.now()
    const cached = this.variantCache.get(variantId)
    
    // Return cached data if it exists and hasn't expired
    if (cached && (now - cached.timestamp) < this.cacheExpiry) {
      return cached.data
    }
    
    try {
      const response = await fetch(`/variants/${variantId}.js`)
      const data = await response.json()
      
      // Cache the result
      this.variantCache.set(variantId, {
        data,
        timestamp: now
      })
      
      return data
    } catch (error) {
      console.error(`Error fetching variant ${variantId}:`, error)
      return null
    }
  }

  async updateOriginalTotal(cartHtml) {
    try {
      // Get cart data to calculate original total
      const response = await fetch('/cart.js')
      const cart = await response.json()
      
      // Fast calculation - use cached data whenever possible
      let originalTotal = 0
      
      if (cart.items.length === 0) {
        originalTotal = 0
      } else {
        // Get unique variant IDs that we need to fetch
        const variantIds = [...new Set(cart.items.map(item => item.variant_id))]
        const now = Date.now()
        
        // Separate cached vs uncached variants
        const uncachedIds = variantIds.filter(id => {
          const cached = this.variantCache.get(id)
          return !cached || (now - cached.timestamp) >= this.cacheExpiry
        })
        
        // Only fetch uncached variants
        if (uncachedIds.length > 0) {
          const variantPromises = uncachedIds.map(variantId => this.getVariantData(variantId))
          await Promise.all(variantPromises)
        }
        
        // Now calculate using cached data
        cart.items.forEach(item => {
          const cached = this.variantCache.get(item.variant_id)
          const variant = cached ? cached.data : null
          
          if (variant) {
            // Get prices in cents and convert to dollars
            const compareAtPriceCents = variant.compare_at_price || 0
            const regularPriceCents = variant.price || item.price || 0
            
            // Convert from cents to dollars (same format as Liquid template)
            const compareAtPrice = compareAtPriceCents / 100
            const regularPrice = regularPriceCents / 100
            
            if (compareAtPrice > regularPrice) {
              originalTotal += compareAtPrice * item.quantity
            } else {
              originalTotal += regularPrice * item.quantity
            }
          } else {
            // Fallback to cart item price if variant not found
            const regularPrice = item.price / 100
            originalTotal += regularPrice * item.quantity
          }
        })
      }

      // Update the original total display
      const originalTotalElement = document.querySelector('[data-original-total]')
      if (originalTotalElement) {
        const formattedPrice = this.formatMoney(originalTotal)
        originalTotalElement.textContent = formattedPrice
      }
    } catch (error) {
      console.error('Error updating original total:', error)
    }
  }

  formatMoney(amount) {
    // Get money format from the form element
    const form = document.querySelector('[data-money-format]')
    let moneyFormat = '${{amount}}'
    
    if (form && form.dataset.moneyFormat) {
      moneyFormat = form.dataset.moneyFormat
    }
    
    // Format the amount (already in dollars, not cents)
    const formattedAmount = amount.toFixed(2)
    return moneyFormat.replace('{{amount}}', formattedAmount).replace('{{ amount }}', formattedAmount)
  }

  showFeedback(message, type = 'info') {
    // Create or update feedback element
    let feedback = document.querySelector('.cart-feedback')
    
    if (!feedback) {
      feedback = document.createElement('div')
      feedback.className = 'cart-feedback'
      feedback.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        padding: 12px 20px;
        border-radius: 4px;
        color: white;
        font-size: 14px;
        z-index: 10000;
        opacity: 0;
        transition: opacity 0.3s ease;
      `
      document.body.appendChild(feedback)
    }

    // Set message and style based on type
    feedback.textContent = message
    feedback.style.backgroundColor = type === 'error' ? '#e74c3c' : '#27ae60'

    // Show feedback
    feedback.style.opacity = '1'

    // Hide after 3 seconds
    setTimeout(() => {
      feedback.style.opacity = '0'
    }, 3000)
  }
}

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartItemRemove())
} else {
  new CartItemRemove()
}

export default CartItemRemove
